'use client';

import {
  ChangeEvent,
  FC,
  useState,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import { Select } from '@/_components';
import { AssignFirmUserSelectProps } from './AssignFirmUserSelect.types';
import { EntityV2Context } from '@/_contexts/admin/EntityV2Provider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { updateAssignments } from '@/_api/admin-clients';
import { UserContext } from '@/_contexts/UserProvider';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const AssignFirmUserSelect: FC<AssignFirmUserSelectProps> = ({
  isLoading,
  isLabelHidden,
  userId,
  company,
  selectedAdvisorId,
  selectedFirmGroupId,
  readOnly = false,
}) => {
  const [isLocalLoading, setLocalLoading] = useState<boolean>(false);

  const { token } = useContext(UserContext);
  const { firmUserOptions } = useContext(FirmSettingsContext);
  const { permissions } = useContext(UserContext);
  const { loadUsersForTable, selectedUser, setSelectedUser } =
    useContext(CompanyContext);
  const { updateSelectedEntity } = useContext(EntityV2Context);
  const { addToast } = useContext(ToastContext);

  const hasPermissionToUpdateAssignee: boolean = getHasPermission(
    PermissionsMapEnum['user.edit_assignee'],
    permissions,
    company?.id,
  );

  const filteredFirmUserOptions = useMemo(() => {
    return firmUserOptions.filter(
      (u) => !u.firm_group_id || u.firm_group_id === selectedFirmGroupId,
    );
  }, [firmUserOptions, selectedFirmGroupId]);

  const computedSelectedAdvisorId = useMemo(() => {
    if (!selectedAdvisorId) return '';

    const advisorIdString = selectedAdvisorId.toString();
    return firmUserOptions.some((option) => option.value === advisorIdString)
      ? advisorIdString
      : '';
  }, [selectedAdvisorId, firmUserOptions]);

  const onLocalChange = useCallback(
    async (event: ChangeEvent<HTMLSelectElement>) => {
      if (readOnly) return;

      const newAdvisorId: number | undefined = parseInt(event.target.value);
      if (!newAdvisorId) return;

      try {
        setLocalLoading(true);
        const { message } = await updateAssignments({
          token,
          advisorId: newAdvisorId,
          clientIds: [userId],
        });

        if (message === 'Success!') {
          addToast('success', 'Assigned firm user was updated successfully');

          if (selectedUser?.id === userId) {
            setSelectedUser({
              ...selectedUser!,
              assigned_advisor_id: newAdvisorId,
            });
          }
        }
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        loadUsersForTable();

        if (company?.user?.assigned_advisor_id) {
          company.user.assigned_advisor_id = newAdvisorId;
          updateSelectedEntity(company);
        }

        setLocalLoading(false);
      }
    },
    [
      token,
      company,
      userId,
      selectedUser,
      setSelectedUser,
      loadUsersForTable,
      updateSelectedEntity,
      addToast,
      readOnly,
    ],
  );

  return (
    <Select
      key={`assign-firm-user-${userId}-company-${company?.id}-selectedAdvisorId-${selectedAdvisorId}`}
      label="Assigned Firm User"
      items={filteredFirmUserOptions}
      isLabelHidden={isLabelHidden}
      isDisabled={
        isLoading ||
        isLocalLoading ||
        readOnly ||
        !hasPermissionToUpdateAssignee
      }
      selectedValue={computedSelectedAdvisorId}
      onChange={onLocalChange}
      placeholder="None"
    />
  );
};
